import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setEmail('');
      form.reset();
      setTimeout(() => setStatus('idle'), 5000);
      return;
    }

    formData.delete('website_alt');

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9avshtv8hat0f6uqho0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
        form.reset();
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Errore di invio';
        setErrorMsg(msg.includes('spam') || msg.includes('form data is spam') ? 'Invio non riuscito. Riprova.' : msg);
      }
    } catch {
      setStatus('error');
      setErrorMsg('Errore di connessione. Riprova più tardi.');
    }
  };

  return (
    <footer className="bg-background-100 border-t border-background-200/50">
      <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left: Newsletter */}
          <div className="lg:w-[38%]">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Newsletter
            </h3>
            <p className="font-heading text-3xl md:text-4xl text-foreground-600 italic font-light mt-1">
              Aggiornamenti
            </p>
            <p className="text-sm text-foreground-600 mt-4 leading-relaxed max-w-sm">
              Ricevi aggiornamenti settimanali su nuove categorie, guide e offerte esclusive per itaobuy spreadsheet.
            </p>

            <form
              id="newsletter-form"
              onSubmit={handleSubmit}
              data-readdy-form
              className="mt-8 max-w-sm"
            >
              <label htmlFor="footer-email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                Indirizzo Email
              </label>
              <input
                id="footer-email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="il-tuo@email.it"
                className="w-full bg-transparent border-0 border-b border-foreground-300/50 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
              />

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="anti-spam-field"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-5 px-6 py-2.5 rounded-full border border-foreground-300/50 text-sm font-medium text-foreground-700 hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {status === 'loading' ? 'Invio in corso...' : 'Iscriviti'}
              </button>

              {status === 'success' && (
                <p className="mt-3 text-sm text-accent-700">Grazie! Iscrizione completata con successo.</p>
              )}
              {status === 'error' && (
                <p className="mt-3 text-sm text-primary-600">{errorMsg}</p>
              )}
            </form>
          </div>

          {/* Right: Link Columns */}
          <div className="lg:w-[62%] grid grid-cols-1 sm:grid-cols-3 gap-10">
            {/* Column 1: Categories */}
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">Categorie</h4>
              <ul className="space-y-2">
                {categories.map((cat) => (
                  <li key={cat.name}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 2: Guides */}
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">Guide</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/guida-completa" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Guida Completa 2026
                  </Link>
                </li>
                <li>
                  <Link to="/guida-completa#come-funziona" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Come Funziona
                  </Link>
                </li>
                <li>
                  <Link to="/confronto-categorie" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Confronto Categorie
                  </Link>
                </li>
                <li>
                  <Link to="/sicurezza-acquisti" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Sicurezza Acquisti
                  </Link>
                </li>
                <li>
                  <Link to="/guida-completa#faq" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    FAQ
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap font-medium"
                  >
                    Vai al Sito Ufficiale →
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 3: Info */}
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">Informazioni</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/guida-completa" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Chi Siamo
                  </Link>
                </li>
                <li>
                  <Link to="/sicurezza-acquisti" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Privacy &amp; Sicurezza
                  </Link>
                </li>
                <li>
                  <Link to="/confronto-categorie" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Categorie
                  </Link>
                </li>
                <li>
                  <a href="#" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap">
                    Contatti
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-6 border-t border-background-200/70 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Itaobuy Spreadsheet Italia. Tutti i diritti riservati.
          </p>
          <p className="text-xs text-foreground-400">
            Link esterni: <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-foreground-600 transition-colors">kaspreadsheet.com</a>
          </p>
        </div>
      </div>
    </footer>
  );
}